import { useId, useState } from "react";
import { useSearchParams } from "react-router";
import { useMap } from "react-map-gl/mapbox";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  IconButton,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import {
  deleteParamValue,
  updateParamValue,
  usePagePrefs,
} from "@pepsico/pref-permissions";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { noop } from "es-toolkit";

import {
  getGetGrowersGeoByLocationQueryKey,
  useDeleteGrowerGeo,
  useGetGrowersGeoByLocation,
} from "api/growers-geo/growers-geo";
import type {
  GrowerGeoResponse,
  SiteGeoResponse,
  TrialGeoResponse,
} from "api/model";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import { useGetTrialsGeoByLocation } from "api/trials-geo/trials-geo";
import {
  SELECTED_MAP_ITEM_BGCOLOR,
  SELECTED_MAP_ITEM_BORDER_COLOR,
} from "components/PlotMap/utils/display";
import { fitMapToBoundary } from "components/PlotMap/utils/mapCameraUtils";
import { readSelectedGrowerGeoId } from "components/PlotMap/utils/selectionPrefs";
import { usePlotMap } from "context/PlotMap";
import {
  fromBoundaryMultiPolygon,
  getBoundaryAreaHectares,
  hasBoundaryGeoJson,
  isBoundarySelfIntersecting,
} from "context/PlotMap/boundaryUtils";
import { GROWER_PREF_KEY, SITE_PREF_KEY, TRIAL_PREF_KEY } from "defs";
import { getApiErrorMessage } from "utils";

import GrowerBoundaryEditor from "./GrowerBoundaryEditor";

const getRemoveGrowerBoundaryError = getApiErrorMessage(
  "Failed to remove grower boundary.",
);

const formatBoundaryArea = (areaHectares: number | null): string =>
  areaHectares != null ? `${areaHectares.toFixed(2)} ha` : "--";

const getSavedGrowerAreaDisplay = (grower: GrowerGeoResponse): string => {
  if (grower.boundary == null || !hasBoundaryGeoJson(grower.boundary)) {
    return "--";
  }

  const ring = fromBoundaryMultiPolygon(grower.boundary);
  if (ring == null) {
    return "--";
  }

  return formatBoundaryArea(getBoundaryAreaHectares(ring));
};

interface GrowerBoundaryItemProps {
  growerGeoId: number | null;
}

const GrowerBoundaryItem: React.FC<GrowerBoundaryItemProps> = ({
  growerGeoId,
}) => {
  const { setAppDialog } = useAppDialog();
  const { default: map } = useMap();
  const queryClient = useQueryClient();
  const menuId = useId();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = menuAnchorEl != null;
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();
  const {
    boundary: {
      editingGrowerGeoId,
      enterGrowerBoundaryMode,
      isClosed,
      isDirty,
      isGrowerBoundaryMode,
      isSiteBoundaryMode,
      loadSaved,
      points,
    },
  } = usePlotMap();

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: growers = [] } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { data: sites = [] } = useGetSitesGeoByLocation(undefined, {
    query: {
      select: (res): SiteGeoResponse[] => (res.status === 200 ? res.data : []),
    },
  });
  const { data: trials = [] } = useGetTrialsGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): TrialGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { mutateAsync: deleteGrowerGeo } = useDeleteGrowerGeo();
  const creatingCount = useIsMutating({ mutationKey: ["createGrowerGeo"] });
  const deletingCount = useIsMutating({ mutationKey: ["deleteGrowerGeo"] });
  const updatingCount = useIsMutating({
    mutationKey: ["updateGrowerGeoBoundary"],
  });
  const isSaving = creatingCount + deletingCount + updatingCount > 0;

  const grower =
    growerGeoId == null
      ? null
      : (growers.find((item) => item.id === growerGeoId) ?? null);
  const isEditing = isGrowerBoundaryMode && editingGrowerGeoId === growerGeoId;
  const hasGrowerBoundary = hasBoundaryGeoJson(grower?.boundary);
  const growerLabel =
    grower == null
      ? "New grower boundary"
      : grower.grower_label?.trim() || "Unlabelled Grower Boundary";
  const isSelfIntersecting =
    isClosed && points.length >= 3 && isBoundarySelfIntersecting(points);
  const areaDisplay = isEditing
    ? formatBoundaryArea(
        isClosed && !isSelfIntersecting && points.length >= 3
          ? getBoundaryAreaHectares(points)
          : null,
      )
    : grower == null
      ? "--"
      : getSavedGrowerAreaDisplay(grower);
  const canStart =
    !isSiteBoundaryMode &&
    !isSaving &&
    (!isGrowerBoundaryMode || editingGrowerGeoId === growerGeoId) &&
    !isDirty;
  const selectedGrowerGeoId = readSelectedGrowerGeoId(
    prefs,
    growers.map((item) => item.id),
  );
  const isSelected = grower != null && grower.id === selectedGrowerGeoId;
  const growerSites =
    grower == null ? [] : sites.filter((site) => site.grower_id === grower.id);
  const soleSite = growerSites.length === 1 ? growerSites[0] : null;

  const withoutSelectedSite = (params: URLSearchParams): URLSearchParams =>
    deleteParamValue(
      deleteParamValue(params, SITE_PREF_KEY, prefs),
      TRIAL_PREF_KEY,
      prefs,
    );

  const applySelectedGrower = (params: URLSearchParams): URLSearchParams => {
    if (grower == null) {
      return params;
    }

    const next = updateParamValue(
      params,
      GROWER_PREF_KEY,
      prefs,
      String(grower.id),
    );
    if (soleSite == null) {
      return withoutSelectedSite(next);
    }

    const withSite = updateParamValue(
      next,
      SITE_PREF_KEY,
      prefs,
      String(soleSite.id),
    );
    const soleSiteTrials = trials.filter(
      (trial) => trial.site_id === soleSite.id,
    );
    if (soleSiteTrials.length !== 1) {
      return deleteParamValue(withSite, TRIAL_PREF_KEY, prefs);
    }

    return updateParamValue(
      withSite,
      TRIAL_PREF_KEY,
      prefs,
      String(soleSiteTrials[0].id),
    );
  };

  const zoomToGrowerSelection = () => {
    if (soleSite != null) {
      fitMapToBoundary(map, soleSite.boundary);
      return;
    }

    fitMapToBoundary(map, grower?.boundary);
  };

  const handleToggleSelected = () => {
    if (!isReady || grower == null || isSiteBoundaryMode) {
      return;
    }

    setSearchParams(
      (prev) =>
        isSelected
          ? withoutSelectedSite(deleteParamValue(prev, GROWER_PREF_KEY, prefs))
          : applySelectedGrower(prev),
      { replace: true, state: { inPageNav: true } },
    );
    if (!isSelected) {
      zoomToGrowerSelection();
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleStart = () => {
    if (isSiteBoundaryMode || (isGrowerBoundaryMode && isDirty)) {
      return;
    }

    if (grower == null) {
      loadSaved([], "");
      enterGrowerBoundaryMode(null);
      return;
    }

    const savedPoints =
      grower.boundary != null && hasBoundaryGeoJson(grower.boundary)
        ? (fromBoundaryMultiPolygon(grower.boundary) ?? [])
        : [];
    loadSaved(savedPoints, grower.grower_label?.trim() ?? "");
    if (!isSelected && isReady) {
      setSearchParams((prev) => applySelectedGrower(prev), {
        replace: true,
        state: { inPageNav: true },
      });
      zoomToGrowerSelection();
    }
    enterGrowerBoundaryMode(grower.id);
  };

  const handleRemove = async () => {
    if (
      grower == null ||
      isSiteBoundaryMode ||
      (isGrowerBoundaryMode && isDirty) ||
      isSaving
    ) {
      return;
    }

    try {
      const response = await deleteGrowerGeo({ growerGeoId: grower.id });
      if (response.status !== 200) {
        setAppDialog({
          confirmText: "Close",
          message: <>{getRemoveGrowerBoundaryError(response.data)}</>,
          onConfirm: noop,
          open: true,
          title: "Error",
        });
        return;
      }

      if (isSelected && isReady) {
        setSearchParams(
          (prev) =>
            withoutSelectedSite(deleteParamValue(prev, GROWER_PREF_KEY, prefs)),
          { replace: true, state: { inPageNav: true } },
        );
      }
      await queryClient.invalidateQueries({
        queryKey: getGetGrowersGeoByLocationQueryKey({
          location_uuid: locationUuid,
        }),
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove grower boundary.";
      setAppDialog({
        confirmText: "Close",
        message: <>{message}</>,
        onConfirm: noop,
        open: true,
        title: "Error",
      });
    }
  };

  const handleEditFromMenu = () => {
    handleCloseMenu();
    handleStart();
  };

  const handleRemoveFromMenu = () => {
    handleCloseMenu();
    void handleRemove();
  };

  if (growerGeoId != null && grower == null) {
    return null;
  }

  return (
    <ListItem
      disablePadding
      sx={{
        alignItems: "stretch",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        {grower == null ? (
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              flex: 1,
              justifyContent: "space-between",
              minWidth: 0,
            }}
          >
            <Typography noWrap sx={{ minWidth: 0 }} variant="body1">
              {growerLabel}
            </Typography>
            <Typography
              aria-live="polite"
              sx={{ flexShrink: 0 }}
              variant="body2"
            >
              {areaDisplay}
            </Typography>
          </Stack>
        ) : (
          <ListItemButton
            aria-pressed={isSelected}
            disabled={isSiteBoundaryMode}
            onClick={handleToggleSelected}
            sx={{
              alignItems: "center",
              bgcolor: isSelected ? SELECTED_MAP_ITEM_BGCOLOR : undefined,
              borderBottomLeftRadius: isSelected ? 0 : 1,
              borderBottomRightRadius: 1,
              borderTopLeftRadius: isSelected ? 0 : 1,
              borderTopRightRadius: 1,
              flex: 1,
              gap: 2,
              justifyContent: "space-between",
              minWidth: 0,
              px: 1,
              ...(isSelected && {
                "&:hover": { bgcolor: SELECTED_MAP_ITEM_BGCOLOR },
                borderLeftColor: SELECTED_MAP_ITEM_BORDER_COLOR,
                borderLeftStyle: "solid",
                borderLeftWidth: 4,
              }),
            }}
          >
            <Typography noWrap sx={{ minWidth: 0 }} variant="body1">
              {growerLabel}
            </Typography>
            <Typography
              aria-live="polite"
              sx={{ flexShrink: 0 }}
              variant="body2"
            >
              {areaDisplay}
            </Typography>
          </ListItemButton>
        )}
        {grower != null && !isEditing && (
          <>
            <IconButton
              aria-controls={isMenuOpen ? menuId : undefined}
              aria-expanded={isMenuOpen ? "true" : undefined}
              aria-haspopup="menu"
              aria-label={`Boundary actions for ${growerLabel}`}
              disabled={isSiteBoundaryMode}
              onClick={handleOpenMenu}
              size="small"
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              id={menuId}
              onClose={handleCloseMenu}
              open={isMenuOpen}
            >
              {hasGrowerBoundary ? (
                <>
                  <MenuItem
                    aria-label={`Edit boundary for ${growerLabel}`}
                    disabled={!canStart}
                    onClick={handleEditFromMenu}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    aria-label={`Remove boundary for ${growerLabel}`}
                    disabled={!canStart}
                    onClick={handleRemoveFromMenu}
                    sx={{ color: "error.main" }}
                  >
                    Remove
                  </MenuItem>
                </>
              ) : (
                <MenuItem
                  aria-label={`Add boundary for ${growerLabel}`}
                  disabled={!canStart}
                  onClick={handleEditFromMenu}
                >
                  Draw
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Stack>
      {isEditing && <GrowerBoundaryEditor />}
    </ListItem>
  );
};

export default GrowerBoundaryItem;
